import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "@/core/components/logo";
import { badgeVariants } from "@/core/components/ui/badge";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsListWithIndicator,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import { isProductEnabled } from "@/business/product-access";
import { useGetBusiness } from "@/business/query";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import {
  hasAiScriptAccess,
  hasIcmPayrollViewerAccess,
  hasPayrollScriptAccess,
} from "@/core/business/payroll-only";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { useImpersonation } from "@/core/impersonation";
import { useImpersonationBusinessOptions } from "@/core/impersonation/impersonation-options";
import { cn } from "@/core/utils/utils";
import { ONBOARDING_STEP } from "@/onboarding/constants";
import {
  getPayBreakdownPath,
  PAY_BREAKDOWN_PATH,
} from "@/payroll/pay-breakdown/routes";
import { useCanViewPayrollRoster } from "@/payroll/roster/permissions";
import {
  PAYROLL_EDITOR_PATH,
  PAYROLL_SETTINGS_PATH,
} from "@/payroll/settings/routes";
import { PROFITABILITY_PATH } from "@/profitability/routes";
import { useGetMyUser } from "@/user/query";
import { isAdmin, isAdminOrOwner, isApOnly, isTech } from "@/user/utils";
import BusinessSelector from "./business-selector";
import EnvPill from "./env-pill";
import { canShowImpersonationControl } from "./impersonation-access";
import { ImpersonationBanner } from "./impersonation-banner";
import { ImpersonationModal } from "./impersonation-modal";
import ProfileDropdown from "./profile-dropdown";

const NAV_ROUTES = {
  dashboard: "/dashboard",
  referrals: "/referrals",
  activityFeed: "/activity-feed",
  campaigns: "/campaigns",
  spiffs: "/spiffs",
  payBreakdown: PAY_BREAKDOWN_PATH,
  payrollSettings: PAYROLL_SETTINGS_PATH,
  payrollRules: PAYROLL_EDITOR_PATH,
  accountsPayable: "/accounts-payable",
  businessSettings: "/business-settings/:businessId/",
} as const;

const NAV_LABELS = {
  dashboard: "Dashboard",
  referrals: "Referrals",
  activity: "Activity",
  campaigns: "Campaigns",
  spiffs: "Spiffs",
  payBreakdown: "Payroll",
  profitability: "Profitability",
  payrollRules: "Pay Rules",
  accountsPayable: "Accounts Payable",
  settings: "Settings",
} as const;

interface NavItemDef {
  href: string;
  label: string;
  show?: (args: {
    enablePocketflows: boolean;
    canSeeBusinessSettings: boolean;
    isSpiffOnly: boolean;
    hasPayBreakdownAccess: boolean;
    canViewRoster: boolean;
    canEditPayrollRules: boolean;
    enableReferrals: boolean;
    enableAp: boolean;
    canSeePayrollPreview: boolean;
    canSeeApPreview: boolean;
  }) => boolean;
}

const NAV_ITEM_DEFS: NavItemDef[] = [
  {
    href: NAV_ROUTES.dashboard,
    label: NAV_LABELS.dashboard,
    show: ({ isSpiffOnly, enableReferrals }) => !isSpiffOnly && enableReferrals,
  },
  {
    href: NAV_ROUTES.referrals,
    label: NAV_LABELS.referrals,
    show: ({ isSpiffOnly, enableReferrals }) => !isSpiffOnly && enableReferrals,
  },
  {
    href: NAV_ROUTES.activityFeed,
    label: NAV_LABELS.activity,
    show: ({ enableReferrals }) => enableReferrals,
  },
  {
    href: NAV_ROUTES.campaigns,
    label: NAV_LABELS.campaigns,
    show: ({ enablePocketflows, isSpiffOnly, enableReferrals }) =>
      enableReferrals && enablePocketflows && !isSpiffOnly,
  },
  {
    href: NAV_ROUTES.spiffs,
    label: NAV_LABELS.spiffs,
    show: ({ enableReferrals }) => enableReferrals,
  },
  {
    href: NAV_ROUTES.payBreakdown,
    label: NAV_LABELS.payBreakdown,
    show: ({ hasPayBreakdownAccess, canSeePayrollPreview }) =>
      hasPayBreakdownAccess || canSeePayrollPreview,
  },
  {
    href: NAV_ROUTES.accountsPayable,
    label: NAV_LABELS.accountsPayable,
    show: ({ enableAp, canSeeApPreview }) => enableAp || canSeeApPreview,
  },
  {
    href: PROFITABILITY_PATH,
    label: NAV_LABELS.profitability,
  },
  {
    href: NAV_ROUTES.payrollRules,
    label: NAV_LABELS.payrollRules,
    show: ({ canEditPayrollRules }) => canEditPayrollRules,
  },
  {
    href: NAV_ROUTES.payrollSettings,
    label: NAV_LABELS.settings,
    show: ({ canViewRoster }) => canViewRoster,
  },
  {
    href: NAV_ROUTES.businessSettings,
    label: NAV_LABELS.settings,
    show: ({ canSeeBusinessSettings, canViewRoster, canEditPayrollRules }) =>
      canSeeBusinessSettings && !canViewRoster && !canEditPayrollRules,
  },
];

const NavItems = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: userInfo, isLoading: isUserLoading } = useGetMyUser();
  const { isImpersonating } = useImpersonation();

  const {
    businessId,
    selectedBusiness,
    isSpiffOnly,
    isLoading: isBusinessLoading,
  } = useBusiness();
  const { data: currentBusiness } = useGetBusiness(businessId);
  const canEditPayrollRules = currentBusiness?.can_edit_payroll_rules === true;
  const { enabled: enablePocketflowsFlag } = useFeatureFlag(
    FEATURE_FLAG_NAMES.enable_pocketflows,
  );
  const enablePocketflows =
    (selectedBusiness?.is_pocketflows_enabled && enablePocketflowsFlag) ??
    false;
  const hasPayrollScript = hasPayrollScriptAccess(selectedBusiness);
  const hasPayBreakdownAccess =
    hasPayrollScript ||
    hasIcmPayrollViewerAccess(selectedBusiness) ||
    hasAiScriptAccess(selectedBusiness);
  const { settings } = useBusinessProducts();
  const enableAp = isProductEnabled(settings, "accounts_payable");
  const enableReferrals = isProductEnabled(settings, "referrals");

  const { role } = userInfo ?? {};
  const { canViewRoster } = useCanViewPayrollRoster(selectedBusiness, userInfo);

  const canSeeBusinessSettings =
    enableReferrals && !isTech(role) && !!businessId;

  const visibility = {
    enablePocketflows,
    canSeeBusinessSettings,
    isSpiffOnly,
    hasPayBreakdownAccess,
    canViewRoster,
    canEditPayrollRules,
    enableReferrals,
    enableAp,
    canSeePayrollPreview:
      isAdminOrOwner(role) && !isProductEnabled(settings, "payroll"),
    canSeeApPreview: isAdminOrOwner(role) && !enableAp,
  };
  const navItems = NAV_ITEM_DEFS.filter(
    (item) =>
      (!isApOnly(role) || item.href === NAV_ROUTES.accountsPayable) &&
      (item.show?.(visibility) ?? true),
  ).map((item) => ({
    ...item,
    href: item.href.replace(":businessId", businessId ?? ""),
  }));

  const activeTab =
    navItems.find((item) => {
      if (
        item.label === NAV_LABELS.settings &&
        (location.pathname.startsWith("/business-settings") ||
          location.pathname.startsWith(PAYROLL_SETTINGS_PATH))
      )
        return true;
      const after = location.pathname.slice(item.href.length);
      return (
        location.pathname.startsWith(item.href) &&
        (after === "" || after.startsWith("/"))
      );
    })?.href ?? "";

  const getNavTarget = (value: string) => {
    if (value !== PAY_BREAKDOWN_PATH) return value;

    const [page, periodStart] = location.pathname.split("/").filter(Boolean);
    return page === "payroll-details" &&
      periodStart &&
      periodStart !== "statements"
      ? getPayBreakdownPath(periodStart)
      : PAY_BREAKDOWN_PATH;
  };

  const handleTabChange = (value: string) => {
    navigate(getNavTarget(value));
  };

  const tabClassName = cn("h-full px-4 text-lg font-medium", {
    "text-white text-opacity-80 data-[state=active]:text-white":
      isImpersonating,
  });

  if (!userInfo || isUserLoading || isBusinessLoading) {
    return null;
  }

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full">
      <TabsListWithIndicator
        className="h-full"
        containerClassName="h-full"
        hideBottomBorder
      >
        {navItems.map((item) => (
          <TabsTrigger
            key={item.href}
            value={item.href}
            className={tabClassName}
            onClick={() => {
              if (item.href === activeTab && item.href === PAY_BREAKDOWN_PATH) {
                navigate(getNavTarget(item.href));
              }
            }}
          >
            <span className="inline-flex items-center gap-1.5">
              {item.label}
              {item.href === PROFITABILITY_PATH && (
                <span
                  aria-hidden="true"
                  className={cn(
                    badgeVariants({ variant: "orange" }),
                    "bg-orange-50 px-1 py-0 text-[9px] leading-3 text-primary-foreground",
                  )}
                >
                  New
                </span>
              )}
            </span>
          </TabsTrigger>
        ))}
        {/* Hidden tab trigger to handle empty string value - prevents Radix UI from not updating active tab state when no matching trigger exists */}
        <TabsTrigger
          key=""
          value=""
          className="hidden h-full px-4 text-lg font-medium"
        />
      </TabsListWithIndicator>
    </Tabs>
  );
};

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { isImpersonating } = useImpersonation();
  const { data: userInfo } = useGetMyUser();
  const { isTenantUser, businessId, isSpiffOnly } = useBusiness();
  const isOnboardingRoute = location.pathname.startsWith("/onboarding");
  const { data: business, isLoading: isBusinessLoading } =
    useGetBusiness(businessId);
  const [isImpersonationModalOpen, setIsImpersonationModalOpen] =
    useState(false);
  useImpersonationBusinessOptions(
    isAuthenticated && (isAdmin(user?.role) || isImpersonating),
  );

  const openImpersonationModal = () => {
    setIsImpersonationModalOpen(true);
  };

  const { first_name, last_name, role } = userInfo ?? {};
  const initials =
    `${first_name?.[0] ?? ""}${last_name?.[0] ?? ""}`.toUpperCase();

  const showImpersonationButton = canShowImpersonationControl({
    role,
    email: user?.email,
    isImpersonating,
  });

  const isBusinessOnboardingIncomplete =
    business &&
    !isBusinessLoading &&
    !!business.onboarding_step &&
    business.onboarding_step !== ONBOARDING_STEP.completed;

  const isLimitedAccess =
    !isAuthenticated ||
    (!isApOnly(role) && !isSpiffOnly && isBusinessOnboardingIncomplete);

  if (isTech(role)) {
    return (
      <>
        <nav
          className={cn(
            "sticky top-0 z-50 w-full border border-b-primary bg-card",
            {
              "border-b-transparent bg-[#184078]": isImpersonating,
            },
          )}
        >
          <Stack
            direction="row"
            align="center"
            justify="between"
            className="h-16 px-4 sm:px-6"
            fullWidth
          >
            <Stack
              direction="row"
              align="center"
              className="h-full cursor-pointer"
              onClick={() => navigate("/")}
            >
              <Logo className="h-8 w-auto" />
            </Stack>

            <Stack direction="row" align="center" gap="sm">
              <EnvPill />
              {isAuthenticated && user?.email && (
                <ProfileDropdown
                  initials={initials}
                  isLimitedAccess={false}
                  showImpersonationButton={false}
                  openImpersonationModal={openImpersonationModal}
                />
              )}
            </Stack>
          </Stack>
        </nav>
        <ImpersonationBanner onSwitchUser={openImpersonationModal} />
        {isImpersonationModalOpen && (
          <ImpersonationModal
            open={isImpersonationModalOpen}
            onOpenChange={setIsImpersonationModalOpen}
          />
        )}
      </>
    );
  }

  return (
    <>
      <nav
        className={cn(
          "sticky top-0 z-50 w-full border border-b-primary bg-card",
          {
            "border-b-transparent bg-[#184078]": isImpersonating,
          },
        )}
      >
        <Stack
          direction="row"
          align="center"
          justify="between"
          className="h-16 px-4 sm:px-6 lg:px-8"
        >
          <Stack
            direction="row"
            align="center"
            className="h-full cursor-pointer"
            onClick={() => navigate("/")}
          >
            <Logo className="h-8 w-auto" />
          </Stack>
          {!isLimitedAccess && !isOnboardingRoute && <NavItems />}
          <Stack direction="row" align="center" gap="sm">
            {isAuthenticated && isTenantUser && <BusinessSelector />}
            <EnvPill />
            {isAuthenticated && user?.email && (
              <ProfileDropdown
                initials={initials}
                isLimitedAccess={isLimitedAccess}
                showImpersonationButton={showImpersonationButton}
                openImpersonationModal={openImpersonationModal}
              />
            )}
          </Stack>
        </Stack>
      </nav>
      <ImpersonationBanner onSwitchUser={openImpersonationModal} />
      {isImpersonationModalOpen && (
        <ImpersonationModal
          open={isImpersonationModalOpen}
          onOpenChange={setIsImpersonationModalOpen}
        />
      )}
    </>
  );
};

export default NavBar;
